/**
 * Overwatch Intelligence API Service
 * 
 * Provides access to the Overwatch news/social media intelligence database.
 * Used by Tracer to find news and events relevant to loaded operations.
 */

export interface OverwatchArticle {
  id: string;
  title: string;
  date?: string;
  pubdate?: number;
  country?: string;
  city?: string;
  latitude?: number;
  longitude?: number;
  primary_category?: string;
  second_category?: string;
  sentiment?: string;
  sentiment_score?: number;
  source_link?: string;
  article_text?: string;
  relevance?: number;
  // Additional fields for detailed view
  summary?: string;
  source?: string;
  location_name?: string;
  entities?: string[];
  category?: string; // alias for primary_category in some responses
}

export interface OverwatchSearchResponse {
  articles: OverwatchArticle[];
  total_count: number;
  search_type: string;
  filters_applied: Record<string, unknown>;
}

export interface AreaSearchParams {
  min_lat: number;
  max_lat: number;
  min_lon: number;
  max_lon: number;
  categories?: string[];
  keywords?: string;
  days?: number;
  limit?: number;
}

export interface OverwatchCategories {
  primary: string[];
  secondary: string[];
  common: string[];
}

export interface OverwatchStats {
  total_articles: number;
  articles_with_location: number;
  recent_articles_30d: number;
  database: string;
}

const API_BASE = '/api/overwatch';

/**
 * Search articles by keywords, categories, and date range
 */
export async function searchArticles(params: {
  query?: string;
  categories?: string;
  countries?: string;
  days?: number;
  startDate?: string;
  endDate?: string;
  limit?: number;
}): Promise<OverwatchSearchResponse> {
  const searchParams = new URLSearchParams();
  
  if (params.query) searchParams.set('query', params.query);
  if (params.categories) searchParams.set('categories', params.categories);
  if (params.countries) searchParams.set('countries', params.countries);
  if (params.days) searchParams.set('days', params.days.toString());
  if (params.startDate) searchParams.set('start_date', params.startDate);
  if (params.endDate) searchParams.set('end_date', params.endDate);
  if (params.limit) searchParams.set('limit', params.limit.toString());
  
  const response = await fetch(`${API_BASE}/search?${searchParams.toString()}`);
  
  if (!response.ok) {
    throw new Error(`Search failed: ${response.statusText}`);
  }
  
  return response.json();
}

/**
 * Search articles within a geographic bounding box
 */
export async function searchByArea(params: AreaSearchParams): Promise<OverwatchSearchResponse> {
  const response = await fetch(`${API_BASE}/area-search`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  });
  
  if (!response.ok) {
    throw new Error(`Area search failed: ${response.statusText}`);
  }
  
  return response.json();
}

/**
 * Search articles within a radius of a point
 */
export async function searchByRadius(params: {
  lat: number;
  lon: number;
  radiusKm?: number;
  categories?: string;
  keywords?: string;
  days?: number;
  limit?: number;
}): Promise<OverwatchSearchResponse> {
  const searchParams = new URLSearchParams();
  
  searchParams.set('lat', params.lat.toString());
  searchParams.set('lon', params.lon.toString());
  if (params.radiusKm) searchParams.set('radius_km', params.radiusKm.toString());
  if (params.categories) searchParams.set('categories', params.categories);
  if (params.keywords) searchParams.set('keywords', params.keywords);
  if (params.days) searchParams.set('days', params.days.toString());
  if (params.limit) searchParams.set('limit', params.limit.toString());
  
  const response = await fetch(`${API_BASE}/radius-search?${searchParams.toString()}`);
  
  if (!response.ok) {
    throw new Error(`Radius search failed: ${response.statusText}`);
  }
  
  return response.json();
}

/**
 * Semantic search using AI to find related articles
 */
export async function semanticSearch(params: {
  query: string;
  categories?: string;
  days?: number;
  limit?: number;
}): Promise<OverwatchSearchResponse> {
  const searchParams = new URLSearchParams();
  
  searchParams.set('query', params.query);
  if (params.categories) searchParams.set('categories', params.categories);
  if (params.days) searchParams.set('days', params.days.toString());
  if (params.limit) searchParams.set('limit', params.limit.toString());
  
  const response = await fetch(`${API_BASE}/semantic-search?${searchParams.toString()}`);
  
  if (!response.ok) {
    throw new Error(`Semantic search failed: ${response.statusText}`);
  }
  
  return response.json();
}

/**
 * Get a single article by ID
 */
export async function getArticle(articleId: string): Promise<OverwatchArticle & {
  province?: string;
  third_category?: string;
  entities?: {
    attacker?: string;
    target?: string;
    arrested?: string;
  };
  keywords?: string;
}> {
  const response = await fetch(`${API_BASE}/articles/${articleId}`);
  
  if (!response.ok) {
    throw new Error(`Failed to get article: ${response.statusText}`);
  }
  
  return response.json();
}

/**
 * Get available categories from Overwatch database
 */
export async function getCategories(): Promise<OverwatchCategories> {
  const response = await fetch(`${API_BASE}/categories`);
  
  if (!response.ok) {
    throw new Error(`Failed to get categories: ${response.statusText}`);
  }
  
  return response.json();
}

/**
 * Get database statistics
 */
export async function getStats(): Promise<OverwatchStats> {
  const response = await fetch(`${API_BASE}/stats`);
  
  if (!response.ok) {
    throw new Error(`Failed to get stats: ${response.statusText}`);
  }
  
  return response.json();
}

/**
 * Check if Overwatch service is available
 */
export async function checkHealth(): Promise<{
  status: string;
  service: string;
  database_configured: boolean;
}> {
  try {
    const response = await fetch(`${API_BASE}/health`);
    return response.json();
  } catch {
    return {
      status: 'error',
      service: 'Overwatch Intelligence',
      database_configured: false,
    };
  }
}

/**
 * Translate place names to English and get AI context
 * Useful for searching English news articles with foreign place names
 */
export interface TranslationResult {
  original: string;
  english: string;
  detected_language: string;
  context?: string;  // AI-generated description of the place
}

export async function translatePlaceNames(texts: string[], includeContext: boolean = true): Promise<TranslationResult[]> {
  try {
    const response = await fetch(`${API_BASE}/translate-places`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ texts, include_context: includeContext }),
    });
    
    if (!response.ok) {
      console.warn('Translation failed, using original texts');
      return texts.map(t => ({ original: t, english: t, detected_language: 'unknown' }));
    }
    
    const data = await response.json();
    return data.translations || texts.map(t => ({ original: t, english: t, detected_language: 'unknown' }));
  } catch (error) {
    console.warn('Translation error:', error);
    return texts.map(t => ({ original: t, english: t, detected_language: 'unknown' }));
  }
}

/**
 * Calculate bounding box from an array of coordinates
 */
export function calculateBoundingBox(coordinates: Array<{ lat: number; lon: number }>): {
  min_lat: number;
  max_lat: number;
  min_lon: number;
  max_lon: number;
} | null {
  if (!coordinates.length) return null;
  
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLon = Infinity;
  let maxLon = -Infinity;
  
  for (const coord of coordinates) {
    if (coord.lat < minLat) minLat = coord.lat;
    if (coord.lat > maxLat) maxLat = coord.lat;
    if (coord.lon < minLon) minLon = coord.lon;
    if (coord.lon > maxLon) maxLon = coord.lon;
  }
  
  // Add a small buffer (about 10km)
  const buffer = 0.1;
  
  return {
    min_lat: minLat - buffer,
    max_lat: maxLat + buffer,
    min_lon: minLon - buffer,
    max_lon: maxLon + buffer,
  };
}

/**
 * Format pubdate timestamp to readable date
 */
export function formatPubdate(pubdate?: number): string {
  if (!pubdate) return 'Unknown date';
  
  // pubdate is in seconds
  const date = new Date(pubdate * 1000);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Get sentiment color based on sentiment value
 */
export function getSentimentColor(sentiment?: string): string {
  switch (sentiment?.toLowerCase()) {
    case 'positive':
      return '#4caf50';
    case 'negative':
      return '#f44336';
    case 'neutral':
    default:
      return '#9e9e9e';
  }
}

/**
 * Get category color for consistent styling
 */
export function getCategoryColor(category?: string): string {
  const categoryColors: Record<string, string> = {
    'Crime': '#e53935',
    'Military': '#1e88e5',
    'Drugs': '#8e24aa',
    'Politics': '#43a047',
    'Security': '#fb8c00',
    'Terrorism': '#d32f2f',
    'Border': '#6d4c41',
    'Immigration': '#00897b',
    'Violence': '#c62828',
    'Conflict': '#ad1457',
  };
  
  return categoryColors[category || ''] || '#757575';
}

